import React, { useState, useEffect, useCallback } from 'react';
import { adminApi, AdminAccountOpeningRequest } from '../api/admin';
import { CheckCircle, XCircle, Search, RefreshCw } from 'lucide-react';
import { formatPublicIdDisplay } from '../utils/formatPublicId';

type FilterStatus = 'ALL' | 'REJECTED' | 'APPROVED';

const STATUS_STYLES: Record<string, string> = {
  APPROVED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700',
};

function fmt(dateStr?: string) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function AccountHistoryPanel() {
  const [all, setAll]           = useState<AdminAccountOpeningRequest[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [filter, setFilter]     = useState<FilterStatus>('REJECTED');
  const [search, setSearch]     = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await adminApi.getAccountOpeningRequests();
      const reviewed = (res.data || []).filter(
        (r: AdminAccountOpeningRequest) =>
          r.status?.toUpperCase() === 'REJECTED' || r.status?.toUpperCase() === 'APPROVED'
      );
      setAll(reviewed);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const rows = all.filter((r) => {
    const s = r.status?.toUpperCase();
    if (filter !== 'ALL' && s !== filter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return (
        r.clientFullName?.toLowerCase().includes(q) ||
        r.clientEmail?.toLowerCase().includes(q) ||
        r.clientPublicId?.toLowerCase().includes(q) ||
        r.requestedCurrency?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const rejectedCount  = all.filter(r => r.status?.toUpperCase() === 'REJECTED').length;
  const approvedCount  = all.filter(r => r.status?.toUpperCase() === 'APPROVED').length;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-gray-800">Account Request History</h2>
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
            {rejectedCount} rejected
          </span>
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            {approvedCount} approved
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search client…"
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
            />
          </div>
          {/* Status filter */}
          <select
            value={filter}
            onChange={e => setFilter(e.target.value as FilterStatus)}
            className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="REJECTED">Rejected only</option>
            <option value="APPROVED">Approved only</option>
            <option value="ALL">All reviewed</option>
          </select>
          <button
            onClick={fetchData}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-600" />
        </div>
      ) : error ? (
        <div className="px-6 py-8 text-center text-red-600 text-sm">{error}</div>
      ) : rows.length === 0 ? (
        <div className="px-6 py-12 text-center text-gray-400 text-sm">
          No {filter === 'ALL' ? 'reviewed' : filter.toLowerCase()} requests found
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                {['Client', 'Currency', 'Status', 'Submitted', 'Reviewed', 'Processed by', 'Rejection reason'].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((r) => {
                const status = r.status?.toUpperCase();
                return (
                  <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                    {/* Client */}
                    <td className="px-5 py-4">
                      <p className="text-sm font-medium text-gray-900">{r.clientFullName || '—'}</p>
                      {r.clientPublicId && (
                        <p className="text-xs text-blue-600 font-mono mt-0.5">{formatPublicIdDisplay(r.clientPublicId)}</p>
                      )}
                      {r.clientEmail && (
                        <p className="text-xs text-gray-400 mt-0.5">{r.clientEmail}</p>
                      )}
                    </td>

                    {/* Currency */}
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">
                        {r.requestedCurrency}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-600'}`}>
                        {status === 'APPROVED'
                          ? <CheckCircle className="w-3 h-3" />
                          : <XCircle className="w-3 h-3" />}
                        {r.status}
                      </span>
                    </td>

                    {/* Submitted */}
                    <td className="px-5 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {fmt(r.createdAt)}
                    </td>

                    {/* Reviewed at */}
                    <td className="px-5 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {fmt(r.reviewedAt)}
                    </td>

                    {/* Processed by */}
                    <td className="px-5 py-4 whitespace-nowrap">
                      {r.reviewedByAdminName ? (
                        <span className="text-sm text-gray-800 font-medium">{r.reviewedByAdminName}</span>
                      ) : (
                        <span className="text-xs text-gray-400 italic">—</span>
                      )}
                    </td>

                    {/* Rejection reason */}
                    <td className="px-5 py-4 max-w-xs">
                      {r.reviewNotes ? (
                        <p className="text-sm text-gray-700 leading-snug">{r.reviewNotes}</p>
                      ) : (
                        <span className="text-xs text-gray-400 italic">No reason provided</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
